import { ArrowIcon } from '@chegg-ui/theme'
import { Tooltip } from '@chegg-ui/tooltip'
import { BaseCardCTAProps } from '@src/components/Cards/CardActions'
import { GrammarReplacement } from '@src/types/checks'
import { OWL_RULE_LINK } from '@src/utils/dataTestIds'
import { getCardAcceptTestId, getCardRejectTestId } from '@src/utils/dataTestIds/getters'
import { decodeHtml } from '@src/utils/decode'
import * as React from 'react'
import OwlLogo from './assets/owllogo_black.png'
import { CardBodyProps } from './CardBody'
import {
  OWL_LINK_SOURCE,
  OwlUrlCardUtm,
  PARAPHRASE,
  REPLACEMENT_VALUE_THRESHOLD_LENGTH,
  TRUNCATE_LENGTH,
  UNSUPPORTED_CLAIMS,
} from './constants'
import { DemoSuggestionText } from './DemoSuggestionText'
import {
  CommentText,
  CommentTextContainer,
  Container,
  ContentContainer,
  DeleteIcon,
  ErrorTextLarge,
  ErrorWrapper,
  OwlImage,
  OwlRuleAnchor,
  OwlRuleContainer,
  ReplacementButtonStyle,
  ReplacementButtonWrapper,
  ReplacementList,
  ReplacementListItem,
  ToolTipText,
  TrashButton,
  TrashContainerCTA,
  VisuallyHidden,
} from './styles'
import { GrammarCardState, OwlLinkAdditionalData } from './types'
import UnsupportedClaimsButtons from './UnsupportedClaimsButtons'
import { ruleOwlCategoryMap, truncateTextWithQuotes } from './utils'

const IconSize = 16
const TrashIconSize: number = IconSize * 1.5

const setSelectedReplacementClick = (
  replacement: GrammarReplacement | undefined,
  setSelectedReplacement: (r: GrammarReplacement | undefined, cb?: () => void) => void,
  handleAccept?: () => void
) => {
  if (replacement) {
    setSelectedReplacement(replacement, handleAccept)
  } else if (handleAccept) {
    handleAccept()
  }
}

const CommentSection: React.FunctionComponent<{
  renderLocation: string
  message: string
}> = ({ renderLocation, message }) => (
  <CommentTextContainer
    data-test={`${renderLocation}-message`}
    addBottomMargin={renderLocation === 'before'}
  >
    <CommentText>{message}</CommentText>
  </CommentTextContainer>
)

interface ReplacementButtonProps {
  replacement: GrammarReplacement | undefined
  index: number
  id: string
  'aria-labelledby': string
  acceptText: string
  setSelectedReplacement: (r: GrammarReplacement | undefined, cb?: () => void) => void
  handleAccept?: () => void
  isDisabled?: boolean
  showTooltip?: boolean
  isParaphrase?: boolean
  isUnsupportedClaim?: boolean
  children: React.ReactNode
}

const ReplacementButton: React.FunctionComponent<ReplacementButtonProps> = ({
  replacement,
  index,
  id,
  'aria-labelledby': ariaLabelledBy,
  isDisabled,
  showTooltip,
  acceptText,
  setSelectedReplacement,
  handleAccept,
  isParaphrase,
  isUnsupportedClaim,
  children,
}) => {
  const exceedsThresholdLength =
    replacement &&
    replacement.value &&
    replacement.value.length > REPLACEMENT_VALUE_THRESHOLD_LENGTH
  const disableHoverAnimation = !!exceedsThresholdLength

  if (isUnsupportedClaim) {
    return (
      <UnsupportedClaimsButtons
        index={index}
        id={id}
        aria-labelledby={ariaLabelledBy}
        isDisabled={isDisabled}
        acceptText={acceptText}
        handleAccept={handleAccept}
      />
    )
  }

  const button = (
    <ReplacementButtonStyle
      disabled={!!isDisabled}
      disableHoverAnimation={disableHoverAnimation}
      isParaphrase={isParaphrase}
      id={replacement && `--accept-label-${index}-${id}`}
      aria-labelledby={`--accept-label-${index}-${id} ${ariaLabelledBy}`}
    >
      {children}
      {!replacement && (
        <VisuallyHidden id={`--accept-label-${index}-${id}`}>
          I fixed the issue
        </VisuallyHidden>
      )}
    </ReplacementButtonStyle>
  )

  return (
    <ReplacementButtonWrapper
      data-test={getCardAcceptTestId(acceptText, replacement ? index : undefined)}
      onClick={() =>
        !isDisabled &&
        setSelectedReplacementClick(replacement, setSelectedReplacement, handleAccept)
      }
    >
      {showTooltip && (
        <Tooltip position="bottom" targetElement={button}>
          <ToolTipText>{acceptText}</ToolTipText>
        </Tooltip>
      )}
      {!showTooltip && button}
    </ReplacementButtonWrapper>
  )
}

const OwlRuleLink: React.FunctionComponent<{
  rule: string
  openExternalLink?: (
    source: string,
    key: string,
    additionalData: OwlLinkAdditionalData
  ) => void
}> = ({ rule, openExternalLink }) => {
  const ruleLowerCase = rule.toLowerCase()
  const owlRuleCategory = ruleOwlCategoryMap.get(ruleLowerCase)

  if (!owlRuleCategory) {
    return null
  }

  const href = owlRuleCategory.url + OwlUrlCardUtm
  const handleOnClick = (e: React.MouseEvent) => {
    e.preventDefault()
    openExternalLink && openExternalLink(href, OWL_LINK_SOURCE, { rule: ruleLowerCase })
  }

  return (
    <OwlRuleContainer>
      <OwlImage src={OwlLogo} alt="Owl logo" />
      <OwlRuleAnchor data-test={OWL_RULE_LINK} onClick={handleOnClick} href={href}>
        {owlRuleCategory.category}
      </OwlRuleAnchor>
    </OwlRuleContainer>
  )
}

export const GrammarCardBody: React.FunctionComponent<CardBodyProps &
  BaseCardCTAProps &
  GrammarCardState> = ({
  replacements,
  errorText,
  acceptText,
  message,
  rule,
  hasBeenModifiedFromEditor,
  acceptStatusText,
  openExternalLink,
  'aria-labelledby': ariaLabelledBy,
  id,
  showDemoSuggestionText = false,
  ...props
}) => {
  const hasReplacements = replacements && replacements.length > 0
  const isParaphraseOrRewrite = rule === PARAPHRASE || !hasReplacements
  const isParaphrase = rule === PARAPHRASE
  const isUnsupportedClaim = rule === UNSUPPORTED_CLAIMS

  const showCommentWhen = {
    beforeReplacements: isParaphraseOrRewrite,
    afterReplacements: !isParaphraseOrRewrite,
  }

  const showArrow = hasReplacements && !isParaphraseOrRewrite
  const shouldFlexColumn = isParaphraseOrRewrite

  const modifiedErrorText = isParaphrase
    ? truncateTextWithQuotes(errorText, TRUNCATE_LENGTH)
    : errorText

  return (
    <Container>
      {showDemoSuggestionText && <DemoSuggestionText />}
      <ContentContainer flexDirection={shouldFlexColumn ? 'column' : 'row'}>
        <ErrorWrapper>
          <ErrorTextLarge>{decodeHtml(modifiedErrorText)}</ErrorTextLarge>
          {showArrow && <ArrowIcon size="medium" pointing="right" />}
        </ErrorWrapper>
        {showCommentWhen.beforeReplacements && (
          <CommentSection renderLocation="before" message={message} />
        )}
        {hasReplacements && (
          <ReplacementList>
            {replacements.map((replacement, index) => (
              <ReplacementListItem key={`replacement-${index}`}>
                <ReplacementButton
                  replacement={replacement}
                  index={index}
                  id={id}
                  aria-labelledby={ariaLabelledBy}
                  showTooltip={true}
                  acceptText={acceptText}
                  isParaphrase={isParaphrase}
                  {...props}
                >
                  <VisuallyHidden>use replacement,</VisuallyHidden> {replacement.value}{' '}
                  <VisuallyHidden>, to fix the issue</VisuallyHidden>
                </ReplacementButton>
              </ReplacementListItem>
            ))}
          </ReplacementList>
        )}
        {!hasReplacements && (
          <ReplacementButton
            replacement={undefined}
            index={0}
            id={id}
            aria-labelledby={ariaLabelledBy}
            isDisabled={!hasBeenModifiedFromEditor}
            showTooltip={false}
            acceptText={acceptText}
            isUnsupportedClaim={isUnsupportedClaim}
            {...props}
          >
            {hasBeenModifiedFromEditor ? acceptStatusText : acceptText}
          </ReplacementButton>
        )}
      </ContentContainer>
      {showCommentWhen.afterReplacements && (
        <CommentSection renderLocation="after" message={message} />
      )}
      <OwlRuleLink rule={rule} openExternalLink={openExternalLink} />
    </Container>
  )
}

export const GrammarCardCTA: React.FunctionComponent<BaseCardCTAProps> = ({
  handleIgnore,
  ignoreText,
  'aria-labelledby': ariaLabelledBy,
  id,
}) => (
  <TrashContainerCTA trashIconSize={TrashIconSize}>
    <Tooltip
      position="bottom"
      targetElement={
        <TrashButton
          onClick={handleIgnore}
          data-test={getCardRejectTestId(ignoreText)}
          aria-labelledby={`--dismiss-label-${id} ${ariaLabelledBy}`}
        >
          <DeleteIcon size="medium" aria-hidden={true} />
        </TrashButton>
      }
    >
      <ToolTipText id={`--dismiss-label-${id}`}>
        {ignoreText}
        <VisuallyHidden>the issue</VisuallyHidden>
      </ToolTipText>
    </Tooltip>
  </TrashContainerCTA>
)



// WEBPACK FOOTER //
// ./src/components/Cards/GrammarCard.tsx